'use client';

import { useEffect, useRef } from 'react';
import { useInView, useMotionValue, useSpring, motion } from 'framer-motion';

interface CountUpProps {
    to: number;
    from?: number;
    duration?: number;
    className?: string;
    decimals?: number;
}

export default function CountUp({
    to,
    from = 0,
    duration = 2,
    className = '',
    decimals = 0,
}: CountUpProps) {
    const ref = useRef<HTMLSpanElement>(null);
    const isInView = useInView(ref, { once: true, margin: "-50px" });
    
    const count = useMotionValue(from);
    const springCount = useSpring(count, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    useEffect(() => {
        if (isInView) {
            count.set(to);
        }
    }, [count, isInView, to]);

    useEffect(() => {
        const unsubscribe = springCount.on('change', (latest) => {
            if (ref.current) {
                ref.current.textContent = latest.toFixed(decimals);
            }
        });
        return unsubscribe;
    }, [springCount, decimals]);

    /**
     * Server-render the FINAL value, then drop back to the start on the client.
     *
     * This used to render `from` — so the homepage's "nibras in numbers" band
     * shipped `0`, `0`, `0`, `0` in its HTML. Crawlers and LLMs read the static
     * document, so the site was advertising five years of work and fifty clients
     * as zero. Those compact facts are exactly what an answer engine extracts to
     * judge whether an agency is established.
     *
     * The reset runs after hydration and only while the element is still out of
     * view, and every current usage sits below the fold, so nobody sees it snap.
     */
    useEffect(() => {
        if (!isInView && ref.current) {
            ref.current.textContent = from.toFixed(decimals);
        }
        // Deliberately mount-only: re-running on isInView would fight the spring.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <motion.span ref={ref} className={className}>
            {to.toFixed(decimals)}
        </motion.span>
    );
}
